<template lang="html">
  <div class="account-details">
    <!-- reCAPTCHA container (invisible) -->
    <div class="hero is-small has-text-centered has-border-bottom">
      <div class="hero-body">
        <div class="has-text-grey-medium">
          <icon name="profile" scale="5" label="set"></icon>
        </div>
        <h1 class="title is-padded-small">{{ userInfo.displayName }}</h1>
        <h3 v-if="customer" class="subtitle">{{ customer.name }}</h3>
      </div>
    </div>
    <section class="section is-vertical-paddingless">
      <cw-list-cell
        class="has-border-bottom"
        title="Email"
        :subtitle="userInfo.email"
        :showDisclosureIndicator="false"
      >
        <div slot="right-area">
          <span
            v-if="emailVerified"
            class="tag is-medium is-rounded"
            style="margin-right: 8px"
          >
            <span class="icon is-small">
              <i class="las la-check-circle"></i>
            </span>
            <span>Verified</span>
          </span>
          <button
            v-else
            class="button is-medium is-primary is-outlined is-rounded"
            @click="sendVerificationEmail"
            :disabled="sendingVerification"
          >
            <span class="icon is-small" v-if="!sendingVerification">
              <i class="las la-envelope"></i>
            </span>
            <span>{{ sendingVerification ? 'Sending...' : 'Verify' }}</span>
          </button>
        </div>
      </cw-list-cell>

      <cw-list-cell
        class="has-border-bottom"
        title="Phone"
        :subtitle="formattedPhone"
        :showDisclosureIndicator="false"
      />
      <cw-list-cell
        class="has-border-bottom"
        v-if="userInfo.secondaryPhone"
        title="Secondary Phone"
        :subtitle="userInfo.secondaryPhone"
        :showDisclosureIndicator="false"
      />
      <cw-list-cell
        class="has-border-bottom"
        title="Two-Factor Authentication"
        :showDisclosureIndicator="false"
      >
        <div slot="right-area">
          <span
            v-if="mfaEnabled"
            class="tag is-medium is-rounded"
            style="margin-right: 8px"
          >
            <span class="icon is-small">
              <i class="las la-check-circle"></i>
            </span>
            <span>Enabled</span>
          </span>
          <button
            v-else
            class="button is-medium is-primary is-outlined is-rounded"
            @click="enrollMFA"
          >
            <span class="icon is-small" v-if="!sendingVerification">
              <i class="las la-lock"></i>
            </span>
            <span>Enable</span>
          </button>
        </div>
      </cw-list-cell>

      <cw-list-cell
        class="has-border-bottom has-text-primary"
        title="Change Password"
        :showDisclosureIndicator="false"
        @click="changePassword"
      ></cw-list-cell>
    </section>
    <section class="section is-bottom-paddingless">
      <cw-list-cell
        class="has-border-bottom has-text-grey-light"
        title="Email Confirmation Settings"
        :showDisclosureIndicator="false"
      />

      <cw-switch-cell
        class="has-border-bottom"
        title="Order Recieved"
        :switchOn="
          prefs && prefs.emailAlerts && prefs.emailAlerts.orderReceived
        "
        @update:switchOn="updateOrderReceived"
      />

      <cw-switch-cell
        class="has-border-bottom"
        title="Order Complete"
        :switchOn="
          prefs && prefs.emailAlerts && prefs.emailAlerts.orderComplete
        "
        @update:switchOn="updateOrderComplete"
      />
      <div class="has-text-centered is-padded-large buttons">
        <a
          v-if="canInviteUsers || canViewUsers"
          class="button is-outlined is-black is-large is-rounded"
          @click="invite"
        >
          <span class="icon">
            <i class="las la-plus"></i>
          </span>
          <span>Invite Users</span>
        </a>
        <a
          class="button is-outlined is-black is-large is-rounded"
          :class="{ 'is-fullwidth': !(canInviteUsers || canViewUsers) }"
          @click="logOut"
        >
          <span class="icon is-medium">
            <i class="las la-1x la-sign-out-alt"></i>
          </span>
          <span>Sign Out</span>
        </a>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import authRequired from '@/components/mixins/auth-required';
import permissionsRequired from '@/components/mixins/permissions-required';
import mfaEnrollment from '@/components/mixins/mfa-enrollment';
import { CUSTOMER_QUERY } from '@/graphql/constants';

export default {
  name: 'account-details',
  // share common functionality with component mixins
  mixins: [authRequired, permissionsRequired, mfaEnrollment],
  props: {},
  // variables
  data() {
    return {
      emailVerified: false,
      sendingVerification: false,
      mfaEnabled: false,
    };
  },
  computed: {
    formattedPhone() {
      let regex = /(\+)(\d{1})(\d{3})(\d{3})(\d{4})/g;
      let phone = this.userInfo && this.userInfo.phoneNumber;
      return (phone && phone.replace(regex, '($3) $4-$5')) || '';
    },
    ...mapState('user', {
      prefs: state => state.prefs,
    }),
  },
  components: {},
  // methods
  watch: {},
  async mounted() {
    // Check email verification status on mount
    await this.checkEmailVerification();
    // Check MFA enrollment status
    await this.checkMFAStatus();
  },
  apollo: {
    customer: {
      query: CUSTOMER_QUERY,
      loadingKey: 'loadingCustomer',
      error(err) {
        window.console.error('auth-required.js - Error loading customer', err);
      },
    },
  },
  methods: {
    updateOrderReceived(newVal) {
      let emailAlerts = this.prefs.emailAlerts;
      emailAlerts.orderReceived = newVal;
      let prefs = { emailAlerts };
      this.$store.commit('user/updatePrefs', { prefs });
    },
    updateOrderComplete(newVal) {
      let emailAlerts = this.prefs.emailAlerts;
      emailAlerts.orderComplete = newVal;
      let prefs = { emailAlerts };
      this.$store.commit('user/updatePrefs', { prefs });
    },
    logOut() {
      this.$store.dispatch('user/logOut');
      this.$modal.show('alert', { title: 'Successfully logged out' });
      setTimeout(() => {
        this.$modal.hide('alert');
      }, 800);
      this.$router.push({ path: '/' });
    },
    invite() {
      this.$modal.show('InviteUsers');
    },
    async checkMFAStatus() {
      try {
        const factors = await this.$store.dispatch('user/getMFAFactors');
        this.mfaEnabled = factors && factors.length > 0;
      } catch (error) {
        window.console.error('Error checking MFA status:', error);
        this.mfaEnabled = false;
      }
    },
    async enrollMFA() {
      // Use the mixin's startMFAEnrollment method
      await this.startMFAEnrollment();
    },
    changePassword() {
      this.$modal.show('ChangePassword');
    },
    async checkEmailVerification() {
      try {
        this.emailVerified = await this.$store.dispatch(
          'user/checkEmailVerified'
        );
      } catch (error) {
        window.console.error('Error checking email verification:', error);
      }
    },
    async sendVerificationEmail() {
      this.sendingVerification = true;
      try {
        const result = await this.$store.dispatch('user/sendEmailVerification');

        if (result.alreadyVerified) {
          this.$modal.show('alert', {
            title: 'Email Already Verified',
            message: 'Your email address is already verified.',
          });
          this.emailVerified = true;
        } else {
          this.$modal.show('alert', {
            title: 'Verification Email Sent',
            message:
              'Please check your email and click the verification link. Refresh this page after verifying.',
          });
        }
      } catch (error) {
        this.$modal.show('alert', {
          title: 'Error',
          message: 'Failed to send verification email. Please try again later.',
        });
        window.console.error('Error sending verification email:', error);
      } finally {
        this.sendingVerification = false;
      }
    },
  },
};
</script>

<style lang="stylus" scoped></style>
